import { useState, useEffect, useMemo } from 'react';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { ChartProps } from '../Interfaces/Charts';
import { Button } from 'react-bootstrap';
import { Download } from '../Utils/Downloader';
import VCenteredModal from '../Components/VCenteredModal';
import { DESCRIPTION_MESSAGE, DOWNLOAD_MESSAGE, POPOUT_MESSAGE, REALTIME_BINDING_CONSTRAINTS_SUFFIX } from '../Constants';


export default function RealTimeBindingConstraints(props: ChartProps) {
    // ***************************************************************************************************************************************************
    // *** Custom Block: the following two defintions are custom to each table this code can produce,
    //      they provide the location for the data and the names of the columns to display
    const api_url = `${process.env.REACT_APP_PUBLIC_API_URL}${REALTIME_BINDING_CONSTRAINTS_SUFFIX}`;
    const download_filename = 'RealTimeBindingConstraints';
    const popout_path = '/charts/realtimebindingconstraints';
    const modalHeader: React.ReactNode = <span>Real-Time Binding Constraints</span>;

    interface IRow {
        Name: string;
        Period: string;
        Price: number | string;
        OVERRIDE: string;
        CURVETYPE: string;
        BP1: number | string;
        PC1: number | string;
        BP2: number | string;
        PC2: number | string;
    }

    function extractRowDataAndIntervalString(json: any) {
        const rowData = json.Constraint ?? [];
        const intervalString = json.RefId ?? '';
        return { rowData, intervalString };
    }

    const descriptionBody: React.ReactNode = useMemo((): React.ReactNode => {
        return (
            <>
                <span>
                    The Real-Time Binding Transmission Constraints table* shows a transmission element that is experiencing congestion and the related market cost. Information on
                    this webpage is updated every five minutes.
                </span>
                <br />
                <br /> <span>This table is updated every 5 minutes.</span>
            </>
        );
    }, []);
    // ***************************************************************************************************************************************************

    const [buttonClicked] = [props.buttonClicked];
    const [showModal, setShowModal] = useState(false);
    const [modalBody, setModalBody] = useState<React.ReactNode>(<></>);
    const [rawData, setRawData] = useState('');

    useEffect(() => {
        let downloadBody: React.ReactNode = (
            <div className='download-modal'>
                <Button className='download-btn' onClick={() => Download(api_url, download_filename, 'json')}>
                    Download JSON
                </Button>
            </div>
        );

        switch (buttonClicked) {
            case DESCRIPTION_MESSAGE:
                setModalBody(descriptionBody);
                setShowModal(true);
                break;
            case DOWNLOAD_MESSAGE:
                setModalBody(downloadBody);
                setShowModal(true);
                break;
            case POPOUT_MESSAGE:
                const newWinddow = window.open(popout_path, '_blank', 'width=800,height=600,noopener,noreferrer');
                if (newWinddow) newWinddow.opener = null;
                break;
            default:
                break;
        }
    }, [buttonClicked, rawData, api_url, descriptionBody]);

    const useFetchJson = <T,>(url: string) => {
        const [data, setData] = useState<T[]>();
        const [loading, setLoading] = useState(false);
        const [dataInterval, setDataInterval] = useState<string>('');
        const [error, setError] = useState<string>('');

        useEffect(() => {
            let intervalId: NodeJS.Timeout;
            const fetchData = async () => {
                setLoading(true);
                setError(''); // Reset error state before new fetch
                try {
                    const response = await fetch(url);
                    if (!response.ok) throw new Error();
                    const json = await response.json();
                    setRawData(json);
                    const { rowData, intervalString } = extractRowDataAndIntervalString(json);
                    setDataInterval(intervalString);
                   // Coerce numeric-looking fields so sorting is numeric
                   const numericFields = ['Price','BP1','PC1','BP2','PC2'];
                   const coerced = rowData.map((r: any) => {
                       const clone: any = { ...r };
                       numericFields.forEach(f => {
                           if (clone[f] !== undefined) {
                               const v = parseFloat(clone[f]);
                               if (!Number.isNaN(v)) clone[f] = v;
                           }
                       });
                       return clone;
                   });
                   setData(coerced);
                } catch (error) {
                    setData([]); // On error, set data to empty array
                    setError('Data Connection Failed'); // Set error message
                } finally {
                    setLoading(false);
                }
            };
            fetchData();
            intervalId = setInterval(fetchData, 5 * 60 * 1000); // Repeat every 5 minutes
            return () => clearInterval(intervalId);
        }, [url]);

        return { data, loading, dataInterval, error };
    };

    const { data, loading, dataInterval, error } = useFetchJson<IRow>(api_url);

    // Sorting state (controlled)
    const [sort, setSort] = useState<SortDescriptor[]>([]);
    const sortedData = useMemo(
        () => orderBy(data || [], sort),
        [data, sort]
    );

    if (loading) {
        return <div className='loading-and-error-box'>Loading...</div>;
    }
    if (error) {
        return <div className='loading-and-error-box'>{error}</div>;
    }
    if (!data || data.length === 0) {
        return <div className='loading-and-error-box'>No Data Available</div>;
    }

    return (
        <>
            <VCenteredModal show={showModal} onHide={() => setShowModal(false)} headercontent={modalHeader} bodycontent={modalBody} />
            <div className='interval-line'>{dataInterval}</div>
            <div className='full-width-grid-wrapper limited-height-grid-580'>
                <Grid
                    data={sortedData}
                    style={{ width: '100%' }}
                    scrollable="scrollable"
                    resizable
                    sortable
                    sort={sort}
                    onSortChange={(e) => setSort(e.sort)}
                >
                    <Column field="Name" title="Name" width="400px" />
                    <Column field="Price" title="Shadow Price" className="k-text-end" />
                    <Column field="OVERRIDE" title="Override" className="k-text-end" />
                    <Column field="CURVETYPE" title="Curve Type" className="k-text-end" />
                    <Column field="BP1" title="BP1" className="k-text-end" />
                    <Column field="PC1" title="PC1" className="k-text-end" />
                    <Column field="BP2" title="BP2" className="k-text-end" />
                    <Column field="PC2" title="PC2" className="k-text-end" />
                </Grid>
            </div>
        </>
    );
}
